/**
 * Replay tracker — records what a person actually did during a carpool so the
 * run can be played back later in super-admin.
 *
 * Design constraints that shaped this:
 *  - It must never break the app it is watching. Every public call swallows its
 *    own errors, and a failing flush drops the batch instead of retrying forever.
 *  - It must survive the app being backgrounded or killed mid-carpool, which is
 *    exactly the moment worth recording — so the queue flushes on every
 *    background transition, not only on a timer.
 *  - Ordering matters more than timestamps: phone clocks drift and batches
 *    arrive out of order, so every event carries a monotonic `seq` within a
 *    client session.
 */

const FLUSH_INTERVAL_MS = 10000;
const MAX_BATCH = 100;
// Past this the app is holding more telemetry than it can plausibly send —
// drop the oldest rather than grow without bound on a long offline stretch.
const MAX_QUEUE = 500;

const state = {
  enabled: false,
  baseUrl: null,
  token: null,
  app: null,
  platform: null,
  appVersion: null,
  schoolId: null,
  clientSessionId: null,
  seq: 0,
  queue: [],
  timer: null,
  flushing: false,
  screen: null,
};

function randomId() {
  const random = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${random}`;
}

/**
 * Begin recording. Safe to call again on login or school switch — it keeps the
 * same client session so one app launch stays one session in the replay.
 */
export function initReplayTracker({
  baseUrl,
  token,
  app,
  platform = null,
  appVersion = null,
  schoolId = null,
} = {}) {
  try {
    if (!baseUrl || !token || !app) {
      state.enabled = false;
      return;
    }

    state.baseUrl = String(baseUrl).replace(/\/$/, '');
    state.token = token;
    state.app = app;
    state.platform = platform;
    state.appVersion = appVersion;
    state.schoolId = schoolId ? Number(schoolId) : null;
    state.enabled = true;

    if (!state.clientSessionId) {
      state.clientSessionId = randomId();
    }

    if (!state.timer) {
      state.timer = setInterval(() => { flushReplayEvents(); }, FLUSH_INTERVAL_MS);
    }
  } catch {
    state.enabled = false;
  }
}

export function setReplaySchool(schoolId) {
  state.schoolId = schoolId ? Number(schoolId) : null;
}

export function setReplayScreen(screenName) {
  const next = screenName || null;
  if (next === state.screen) return;

  const previous = state.screen;
  state.screen = next;
  trackReplayEvent('nav.screen', { from: previous, to: next });
}

export function getReplayScreen() {
  return state.screen;
}

/**
 * Stop recording and drop anything queued — called on logout so one person's
 * activity can never be flushed under the next person's token.
 */
export function resetReplayTracker() {
  state.enabled = false;
  state.token = null;
  state.queue = [];
  state.clientSessionId = null;
  state.seq = 0;
  state.screen = null;

  if (state.timer) {
    clearInterval(state.timer);
    state.timer = null;
  }
}

/**
 * Queue one event. Synchronous and non-throwing: call it straight from an
 * onPress handler without awaiting or wrapping.
 */
export function trackReplayEvent(eventType, payload = {}, options = {}) {
  try {
    if (!state.enabled || !eventType) return;

    const schoolId = options.schoolId ? Number(options.schoolId) : state.schoolId;
    if (!schoolId) return;

    state.seq += 1;
    state.queue.push({
      school_id: schoolId,
      event_type: eventType,
      occurred_at: new Date().toISOString(),
      seq: state.seq,
      subject_type: options.subjectType || null,
      subject_id: options.subjectId || null,
      client_session_id: state.clientSessionId,
      payload: {
        // Always stamp the screen so an event is readable without reconstructing
        // navigation from the surrounding events.
        screen: state.screen,
        ...payload,
      },
    });

    if (state.queue.length > MAX_QUEUE) {
      state.queue.splice(0, state.queue.length - MAX_QUEUE);
    }
    if (state.queue.length >= MAX_BATCH) {
      flushReplayEvents();
    }
  } catch {
    // Telemetry must never surface an error into the app.
  }
}

/** Convenience wrapper for the most common case: someone tapped something. */
export function trackReplayTap(label, payload = {}, options = {}) {
  trackReplayEvent('ui.tap', { label, ...payload }, options);
}

/**
 * Send whatever is queued.
 *
 * `keepalive` is for the page-unload path on web, where an ordinary fetch is
 * cancelled as the document goes away. Unlike sendBeacon it still carries the
 * Authorization header, so the token never has to travel in a URL.
 */
export async function flushReplayEvents({ keepalive = false } = {}) {
  if (!state.enabled || state.flushing || state.queue.length === 0) return;

  const batch = state.queue.slice(0, MAX_BATCH);
  const body = JSON.stringify({
    app: state.app,
    platform: state.platform,
    app_version: state.appVersion,
    client_session_id: state.clientSessionId,
    events: batch,
  });
  const url = `${state.baseUrl}/replay/events`;

  state.flushing = true;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${state.token}`,
      },
      body,
      // Lets the request outlive the page during an unload flush.
      ...(keepalive ? { keepalive: true } : {}),
    });

    if (response.ok) {
      state.queue = state.queue.slice(batch.length);
    } else if (response.status === 401 || response.status === 403) {
      // The token is gone or not allowed here; retrying would just loop.
      state.queue = [];
    }
    // Any other failure keeps the batch for the next flush.
  } catch {
    // Offline. Keep the batch; MAX_QUEUE bounds how much can pile up.
  } finally {
    state.flushing = false;
  }
}

export function getReplayClientSessionId() {
  return state.clientSessionId;
}

export function isReplayTrackingEnabled() {
  return state.enabled;
}
